import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Save, Loader2, AlertTriangle, ImageIcon, Mail, Globe2, Link2, User } from "lucide-react";
import { academy } from "@/lib/academy";
import { apiErr } from "@/lib/api";
import { FIELDS, PRIORITY_META, computePriority } from "@/lib/triage";
import { Textarea } from "@/components/ui/textarea";

export const SIM_REQUIRED = ["severity", "harm_category", "verifiable", "scope", "urgent", "policy", "next_step", "confidence", "preliminary", "duplicate", "availability", "conflict", "decision"];
export const simComplete = (d) => !!d && SIM_REQUIRED.every((k) => (Array.isArray(d[k]) ? d[k].length : d[k]));

function Meta({ icon: Icon, children, testid }) {
  return <div className="flex items-center gap-2 text-[13px] text-zinc-300" data-testid={testid}><Icon size={14} className="shrink-0 text-zinc-500" /><span className="min-w-0 break-all">{children}</span></div>;
}

function Chips({ f, d, setD }) {
  return (
    <section>
      <div className="mb-1.5 text-[12px] font-semibold text-zinc-200">{f.title}{f.type === "multi" && <span className="ml-1 text-[11px] font-normal text-zinc-500">(multiple allowed)</span>}{!(Array.isArray(d[f.key]) ? d[f.key].length : d[f.key]) && <span className="ml-1 text-red-400">*</span>}</div>
      <div className="flex flex-wrap gap-1.5" data-testid={`sim-field-${f.key}`}>
        {f.options.map((o, i) => {
          const active = f.type === "multi" ? (d[f.key] || []).includes(o) : d[f.key] === o;
          const click = () => setD((p) => {
            if (f.type !== "multi") return { ...p, [f.key]: p[f.key] === o ? "" : o };
            const cur = p[f.key] || [];
            return { ...p, [f.key]: cur.includes(o) ? cur.filter((x) => x !== o) : [...cur, o] };
          });
          return (
            <button key={o} data-testid={`sim-${f.key}-${i}`} onClick={click}
              className={`rounded-md border px-2.5 py-1 text-left text-[12px] transition-colors ${active ? "border-blue-500/60 bg-blue-500/15 text-blue-200" : "border-zinc-800 bg-zinc-950 text-zinc-400 hover:text-zinc-200"}`}>{o}</button>
          );
        })}
      </div>
    </section>
  );
}

export function OpsTicket({ t, draft, decisions, onSaved }) {
  const [d, setD] = useState(draft || {});
  const [busy, setBusy] = useState(false);
  useEffect(() => { setD(draft || {}); }, [t.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const pr = useMemo(() => computePriority(d), [d]);
  const save = async () => {
    setBusy(true);
    try {
      const r = await academy.put(`/sim/${t.id}`, { data: d });
      onSaved(t.id, d, r.data.complete);
    } catch (e) { toast.error(apiErr(e?.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div className="p-5" data-testid={`sim-ticket-${t.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="font-mono text-[15px] font-semibold text-zinc-50">{t.reference}</div>
          <div className="mt-0.5 text-[13px] text-zinc-400">{t.platform} · {t.category}</div>
        </div>
        <span className="rounded-md px-2 py-1 text-[12px] font-bold text-white" style={{ backgroundColor: PRIORITY_META[pr].color }} data-testid="sim-priority">{PRIORITY_META[pr].label}</span>
      </div>
      <div className="mt-4 grid gap-2 rounded-lg border border-zinc-800 bg-zinc-950/60 p-3 sm:grid-cols-2">
        <Meta icon={User} testid="sim-reporter-name">{t.reporter_name}</Meta>
        <Meta icon={Mail} testid="sim-reporter-email">{t.reporter_email}</Meta>
        <Meta icon={Globe2}>{t.country}</Meta>
        <Meta icon={Link2}>{t.url || <span className="text-zinc-500">No link provided</span>}</Meta>
      </div>
      <div className="mt-4 text-[11px] font-semibold uppercase tracking-wider text-zinc-500">Reporter's description</div>
      <p className="mt-1 whitespace-pre-line rounded-lg border border-zinc-800 bg-zinc-950/60 p-3 text-[14px] leading-relaxed text-zinc-200" data-testid="sim-description">{t.description}</p>
      <div className="mt-4 text-[11px] font-semibold uppercase tracking-wider text-zinc-500">Evidence ({t.evidence.length})</div>
      <div className="mt-1 grid gap-2 sm:grid-cols-3">
        {t.evidence.length === 0 && <div className="text-[13px] text-zinc-500">No attachments.</div>}
        {t.evidence.map((e) => (
          <div key={e} className="flex gap-2 rounded-lg border border-zinc-800 bg-zinc-900/60 p-3 text-[12px] text-zinc-300"><ImageIcon size={14} className="mt-0.5 shrink-0 text-zinc-500" />{e}</div>
        ))}
      </div>
      <div className="mt-6 flex items-center gap-2 border-t border-zinc-800 pt-5 text-[14px] font-semibold text-zinc-50">Triage checklist <span className="rounded bg-zinc-800 px-1.5 text-[10px] font-normal uppercase text-zinc-400">AI analysis unavailable</span></div>
      {(d.severity || "").startsWith("Critical") && <div className="mt-3 flex gap-2 rounded-lg border border-red-500/40 bg-red-500/10 p-2.5 text-[12px] text-red-200"><AlertTriangle size={15} className="shrink-0" /> Fast path: immediate escalation may be warranted.</div>}
      <div className="mt-4 space-y-5">{FIELDS.map((f) => <Chips key={f.key} f={f} d={d} setD={setD} />)}</div>
      <section className="mt-6 rounded-lg border border-blue-500/30 bg-blue-500/[0.05] p-4">
        <div className="mb-2 text-[13px] font-semibold text-zinc-50">Your decision{!d.decision && <span className="ml-1 text-red-400">*</span>}</div>
        <div className="space-y-1.5" data-testid="sim-decisions">
          {decisions.map((o, i) => (
            <label key={o} className={`flex cursor-pointer items-start gap-2.5 rounded-md border px-3 py-2 text-[13px] ${d.decision === o ? "border-blue-500/60 bg-blue-500/10 text-zinc-50" : "border-zinc-800 text-zinc-300 hover:border-zinc-700"}`}>
              <input type="radio" name={`dec-${t.id}`} data-testid={`sim-decision-${i}`} checked={d.decision === o} onChange={() => setD((p) => ({ ...p, decision: o }))} className="mt-0.5 accent-blue-500" /> {o}
            </label>
          ))}
        </div>
        <Textarea data-testid="sim-note" value={d.note || ""} onChange={(e) => setD((p) => ({ ...p, note: e.target.value }))} placeholder="Internal note (optional, not graded)" className="mt-3 min-h-[60px] border-zinc-800 bg-zinc-950 text-[13px] text-zinc-100" />
      </section>
      <button data-testid="sim-save" onClick={save} disabled={busy} className="mt-4 inline-flex h-10 w-full items-center justify-center gap-2 rounded-lg bg-blue-600 text-[13px] font-semibold text-white hover:bg-blue-500 disabled:opacity-50">
        {busy ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save triage {simComplete(d) ? "& next" : "(draft)"}
      </button>
    </div>
  );
}
